// Pure formatting/labeling helpers for the Code Review queue UI. Kept dependency-
// free (no React, no config, no import.meta) so they can be unit-tested in
// isolation — the timezone normalisation below is load-bearing and regressed
// once already.

// The pr-reviewer (SQLite datetime('now')) emits UTC timestamps WITHOUT a zone,
// e.g. "2026-06-16 18:29:10". Date.parse treats that space-separated form as
// LOCAL time, shifting every value by the viewer's UTC offset — a Prague viewer
// (UTC+2) saw "2h ago" for something that just happened. Normalise to UTC.
export function parseTs(s: string | undefined | null): number {
  // The proxies never check inside an item, so a non-string can arrive here.
  if (typeof s !== "string" || s === "") return NaN;
  if (/[zZ]$|[+-]\d\d:?\d\d$/.test(s)) return Date.parse(s); // already zoned
  const iso = s.includes("T") ? s : s.replace(" ", "T");
  return Date.parse(iso.endsWith("Z") ? iso : `${iso}Z`);
}

export function fmtAgo(iso: string): string {
  const ts = parseTs(iso);
  if (!Number.isFinite(ts)) return "";
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Compact duration label for a span of milliseconds: "8s", "5m", "1h 5m".
export function fmtDur(ms: number): string {
  // NaN guard: a missing/malformed timestamp yields NaN, and Math.max(0, NaN) is
  // NaN (not 0), which would render "NaNh NaNm" in the UI — return "" instead.
  if (!Number.isFinite(ms)) return "";
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export const TERMINAL_STATES = new Set(["done", "needs_human", "failed"]);

// Human-readable label for a pipeline stage. Unknown states fall back to the raw
// value so a newly-added backend state still renders something sensible.
const STAGE_LABELS: Record<string, string> = {
  queued: "Waiting in queue",
  reviewing: "Reviewing the PR changes",
  fixing: "Applying fixes",
  verifying: "Verifying (review panel)",
  gating: "Mechanical checks",
  qa: "Emil — in-game test",
  qa_blocked: "Waiting for the QA desktop",
  verdict: "Final release verdict",
  merging: "Merging to dev",
  done: "Merged — done",
  needs_human: "Stopped — needs a human",
  failed: "Failed",
};
export function stageLabel(state: string): string {
  return STAGE_LABELS[state] ?? state;
}
